<template>
  <vue-tel-input
    ref="phoneInput"
    v-model="phoneNumber"
    :class="`mc-profile-input-number ${!isValid && phoneNumber ? 'is-invalid' : ''}`"
    :valid-characters-only="true"
    :autoFormat="false"
    default-country="ID"
    :onlyCountries="['ID']"
    mode="national"
    :invalidMsg="invalidMsg"
    :disabled="disabled"
    :input-options="{
      placeholder: placeholder,
      showDialCode: true,
      id: id,
      name: name,
      maxlength: 25,
      autofocus: false,
    }"
    @validate="onValidate"
    @input="handleChange"
    @keydown.native="handleKeydown"
    @paste.native="handlePaste"
    @blur="$emit('blur')"
  />
</template>

<script>
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'

export default {
  components: {
    VueTelInput,
  },
  props: {
    invalidMsg: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    onFocus: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isValid: true,
    }
  },
  computed: {
    phoneNumber: {
      get() {
        if (this.value === '') {
          return ''
        }
        if (this.value == null || this.value === undefined) {
          return '+62'
        }
        if (this.value == '0') {
          return ''
        }
        return this.ensurePlusPrefix(this.value)
      },
      set(phoneNo) {
        const sanitized = phoneNo ? phoneNo.replace(/\s+/g, '') : phoneNo
        if (sanitized === '+62' || sanitized === null || sanitized === '') {
          this.$emit('onChange', null)
          return
        }
        this.$emit('onChange', sanitized?.replace('+', ''))
      },
    },
  },
  mounted() {
    // Ensure +62 is displayed on mount
    if (this.onFocus) {
      this.$refs.phoneInput.$refs.input.focus()
    }
    
    // Force +62 display if value is null/empty on mount
    if (this.value == null || this.value === '' || this.value === undefined) {
      this.$nextTick(() => {
        if (this.$refs.phoneInput && this.$refs.phoneInput.$refs.input) {
          this.$refs.phoneInput.$refs.input.value = '+62'
        }
      })
    }
  },
  methods: {
    // Enhanced method to ensure +62 prefix is always present
    ensurePlusPrefix(value) {
      if (!value) {
        return '+62'
      }
      
      // Clean the value: remove all non-digit characters except + at the start
      let cleanValue = String(value)
        .replace(/\s+/g, '') // Remove whitespace
        .replace(/[!@#$%^&*()_=\[\]{};':"\\|,.<>?`~\-]/g, '') // Remove symbols
        .replace(/[a-zA-Z]/g, '') // Remove letters
        .replace(/\+(?!^)/g, '') // Remove + except at the beginning
      
      // Extract only digits for processing
      const digitsOnly = cleanValue.replace(/\D/g, '')
      
      if (!digitsOnly) {
        return '+62'
      }
      
      // If digits start with 62, use as is with +
      if (digitsOnly.startsWith('62')) {
        return '+' + digitsOnly
      }
      
      // For any other case, prepend +62
      return '+62' + digitsOnly
    },

    // Enhanced validation to work with forced +62
    onValidate(phoneObject) {
      const value = this.phoneNumber || ''
      const digits = value.replace(/\D/g, '')
      const national = phoneObject?.nationalNumber || ''

      if (digits === '62') {
        this.isValid = true
        this.$emit('onValid', this.isValid)
        return
      }

      let valid = true

      // Check for whitespace
      if (/\s/.test(value)) valid = false
      
      // Check length (including country code)
      if (digits.length < 10 || digits.length > 17) valid = false
      
      // Check if national number starts with 0 (invalid for Indonesian numbers)
      if (national.startsWith('0')) valid = false
      
      // Ensure it starts with 62 (Indonesian country code)
      if (!digits.startsWith('62')) valid = false

      this.isValid = valid
      this.$emit('onValid', this.isValid)
    },

    handleChange(e) {
      const dial = '+62'
      
      // Force +62 prefix if not present
      if (!e.startsWith(dial)) {
        const correctedValue = this.ensurePlusPrefix(e)
        this.phoneNumber = correctedValue
        this.$nextTick(() => {
          this.$refs.phoneInput.$refs.input.value = correctedValue
        })
        return
      }
      
      if (e === dial) {
        this.isValid = true
      }
      else if (e.length - 3 < 7 || e.length - 3 > 14) { // Adjusted for Indonesian phone length
        this.isValid = false
      }
      this.$emit('onValid', this.isValid)
    },

    handleKeydown(e) {
      const input = e.target
      const val = input?.value || ''
      const caretStart = input?.selectionStart ?? 0
      const caretEnd = input?.selectionEnd ?? caretStart
      const dial = '+62'

      // Allow navigation and control keys always
      const allowedKeys = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
      ]

      // Allow Ctrl/Cmd combinations
      const isCtrlCmd = e.ctrlKey || e.metaKey
      const allowedCtrlKeys = ['a', 'c', 'v', 'x', 'z', 'y']
      
      if (isCtrlCmd && allowedCtrlKeys.includes(e.key?.toLowerCase?.())) {
        // Handle cut operation specially to protect +62
        if (e.key?.toLowerCase?.() === 'x') {
          if (caretStart < dial.length) {
            e.preventDefault()
            return
          }
        }
        return // Allow other Ctrl/Cmd operations
      }

      // ALWAYS allow Backspace and Delete (we'll handle +62 protection below)
      if (e.key === 'Backspace' || e.key === 'Delete') {
        // Force +62 if value doesn't start with it
        if (!val.startsWith(dial)) {
          const correctedValue = this.ensurePlusPrefix(val)
          this.phoneNumber = correctedValue
          this.$nextTick(() => {
            input.setSelectionRange(dial.length, dial.length)
          })
          return
        }
        
        // Protect +62 prefix from deletion
        if (e.key === 'Backspace') {
          if (caretStart <= dial.length) {
            e.preventDefault()
            this.$nextTick(() => input.setSelectionRange(dial.length, dial.length))
            return
          }
        }
        if (e.key === 'Delete') {
          if (caretStart < dial.length) {
            e.preventDefault()
            this.$nextTick(() => input.setSelectionRange(dial.length, dial.length))
            return
          }
        }
        return // Allow the deletion to proceed
      }

      // Allow other navigation keys
      if (allowedKeys.includes(e.key)) {
        return
      }

      // Block everything except digits
      if (!/^\d$/.test(e.key)) {
        e.preventDefault()
        return
      }

      // From here, we know it's a digit
      // Force +62 if value doesn't start with it
      if (!val.startsWith(dial)) {
        const correctedValue = this.ensurePlusPrefix(val)
        this.phoneNumber = correctedValue
        this.$nextTick(() => {
          input.setSelectionRange(dial.length, dial.length)
        })
        return
      }

      // Prevent leading zero after +62
      if (e.key === '0' && val.startsWith(dial) && caretStart === dial.length) {
        e.preventDefault()
      }
    },

    handlePaste(e) {
      const input = e.target
      const raw = (e.clipboardData || window.clipboardData).getData('text') || ''
      if (!raw) return

      e.preventDefault()

      // Sanitize: remove all non-digit characters except + at the beginning
      let data = raw
        .replace(/\s+/g, '') // Remove whitespace
        .replace(/[!@#$%^&*()_=\[\]{};':"\\|,.<>?`~\-]/g, '') // Remove symbols
        .replace(/[a-zA-Z]/g, '') // Remove letters
        .replace(/\+(?!^)/g, '') // Remove + except at the beginning
      
      // Extract only digits for processing
      const digitsOnly = data.replace(/\D/g, '')
      
      // Don't allow empty paste
      if (!digitsOnly) {
        return
      }
      
      // Force +62 prefix on cleaned data
      const correctedValue = this.ensurePlusPrefix(digitsOnly)
      this.phoneNumber = correctedValue

      this.$nextTick(() => {
        try {
          const newPos = Math.min(correctedValue.length, correctedValue.length)
          input.setSelectionRange(newPos, newPos)
          input.dispatchEvent(new Event('input', { bubbles: true }))
        } catch (err) {
          console.log(err)
        }
      })

      // Validate the corrected value
      if (!/\s/.test(correctedValue) && digitsOnly.length > 0) {
        this.isValid = true
      }
    },
  },
  
  // Watch for value changes from parent and ensure +62 prefix
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        // Always show +62 when value becomes null/empty
        if (newValue == null || newValue === '' || newValue === undefined) {
          this.$nextTick(() => {
            if (this.$refs.phoneInput && this.$refs.phoneInput.$refs.input) {
              this.$refs.phoneInput.$refs.input.value = '+62'
            }
          })
          return
        }
        
        // Ensure +62 prefix for non-empty values
        const corrected = this.ensurePlusPrefix(newValue)
        if (!corrected.startsWith('+62')) {
          this.$nextTick(() => {
            this.$emit('onChange', corrected.replace('+', ''))
          })
        }
      }
    }
  }
}
</script>

<style src="vue-tel-input/dist/vue-tel-input.css"></style>
<style lang="scss">
@import './style.scss';
</style>